// src/components/UserFormModal.js
import React, { useState, useEffect } from 'react';
import '../styles/Cadastro.css';

// Helper para criar um item de benefício/desconto vazio
const createBenefitOrDeductionItem = () => ({ nome: '', valor: '' });

const UserFormModal = ({ user, isNewUser, onClose, onSave, allUsers = [] }) => {
  const initialFormData = {
    name: '',
    email: '',
    // --- NOVOS CAMPOS DE ENDEREÇO ---
    cep: '',
    endereco: '',
    numero: '',
    bairro: '',
    cidade: '',
    estado: '',
    // -------------------------------
    departamento: '',
    role: '', 
    funcao: '', 
    salario: '',
    dataEntrada: '',
    dataNascimento: '',
    loja: '',
    managerEmail: '',
    canViewSubordinates: false,
    modeloContrato: 'CLT',
    status: 'active',
    sendInvite: false,
    beneficios: [createBenefitOrDeductionItem()],
    descontos: [createBenefitOrDeductionItem()],
  };

  const initialExitData = {
    exitDate: new Date().toISOString().split('T')[0],
    reason: '',
    type: '', 
    feedback: '',
    wouldRecommend: 'yes'
  };

  const [formData, setFormData] = useState(initialFormData);
  const [exitData, setExitData] = useState(initialExitData);
  const [loadingCep, setLoadingCep] = useState(false); // Estado visual para busca de CEP

  useEffect(() => {
    const formatDate = (dateValue) => {
        if (!dateValue) return '';
        const dateObject = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
        if (isNaN(dateObject.getTime())) return '';
        return dateObject.toISOString().split('T')[0];
    };

    if (user && !isNewUser) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        // Carrega dados de endereço (se existirem)
        cep: user.cep || '',
        endereco: user.endereco || '',
        numero: user.numero || '',
        bairro: user.bairro || '',
        cidade: user.cidade || '',
        estado: user.estado || '',
        
        departamento: user.departamento || '',
        role: user.role || '',
        funcao: user.funcao || '',
        salario: user.salario || '',
        dataEntrada: formatDate(user.dataEntrada),
        dataNascimento: formatDate(user.dataNascimento),
        loja: user.loja || '',
        managerEmail: user.managerEmail || '',
        canViewSubordinates: user.canViewSubordinates || false,
        modeloContrato: user.modeloContrato || 'CLT',
        status: user.status || 'active',
        sendInvite: false,
        beneficios: user.beneficios && user.beneficios.length > 0 ? user.beneficios.map(b => ({ nome: b.nome || '', valor: b.valor || '' })) : [createBenefitOrDeductionItem()],
        descontos: user.descontos && user.descontos.length > 0 ? user.descontos.map(d => ({ nome: d.nome || '', valor: d.valor || '' })) : [createBenefitOrDeductionItem()],
      });

      if (user.exitData) {
          setExitData({
              exitDate: formatDate(user.exitData.exitDate) || initialExitData.exitDate,
              reason: user.exitData.reason || '',
              type: user.exitData.type || '',
              feedback: user.exitData.feedback || '',
              wouldRecommend: user.exitData.wouldRecommend || 'yes'
          });
      }
    } else { 
      setFormData({
        ...initialFormData,
        sendInvite: true, 
        status: 'active',
      });
    }
  }, [user, isNewUser]);

  // --- FUNÇÃO DE BUSCA DE CEP ---
  const handleBlurCep = async (e) => {
    const cep = e.target.value.replace(/\D/g, '');
    
    if (cep.length !== 8) return;

    setLoadingCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();

      if (!data.erro) {
        setFormData(prev => ({
          ...prev,
          endereco: data.logradouro,
          bairro: data.bairro,
          cidade: data.localidade,
          estado: data.uf
        }));
        // Foca no campo número após carregar
        document.getElementById('numero')?.focus();
      } else {
        alert("CEP não encontrado.");
      }
    } catch (error) {
      console.error("Erro ao buscar CEP:", error);
    } finally {
      setLoadingCep(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleExitDataChange = (e) => {
      const { name, value } = e.target;
      setExitData(prev => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, event, itemType) => {
    const { name, value } = event.target;
    const items = [...formData[itemType]];
    items[index] = { ...items[index], [name]: value }; 
    setFormData(prev => ({ ...prev, [itemType]: items }));
  };
  
  const handleAddItem = (itemType) => {
    setFormData(prev => ({
      ...prev,
      [itemType]: [...prev[itemType], createBenefitOrDeductionItem()]
    }));
  };

  const handleRemoveItem = (index, itemType) => {
    const items = [...formData[itemType]];
    items.splice(index, 1);
    if (items.length === 0) {
        items.push(createBenefitOrDeductionItem());
    }
    setFormData(prev => ({ ...prev, [itemType]: items }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.departamento || !formData.role) {
        alert('Por favor, preencha os campos obrigatórios.');
        return;
    }

    if (formData.status === 'inactive') {
        if (!exitData.exitDate || !exitData.reason || !exitData.type) {
            alert('Para inativar um usuário, é obrigatório preencher a Data, o Tipo e o Motivo do desligamento.');
            return;
        }
    }
    
    const parseSafeDate = (dateString) => {
        if (!dateString) return null;
        const [year, month, day] = dateString.split('-').map(Number);
        return new Date(year, month - 1, day, 12, 0, 0);
    };

    const processItems = (items) => {
        return items
            .map(item => ({ nome: item.nome.trim(), valor: parseFloat(item.valor) || 0 }))
            .filter(item => item.nome !== '');
    };

    const payload = {
        ...formData,
        salario: parseFloat(formData.salario) || 0,
        dataEntrada: parseSafeDate(formData.dataEntrada),
        dataNascimento: parseSafeDate(formData.dataNascimento),
        beneficios: processItems(formData.beneficios),
        descontos: processItems(formData.descontos),
        exitData: formData.status === 'inactive' ? {
            ...exitData,
            exitDate: parseSafeDate(exitData.exitDate)
        } : null
    };
    onSave(payload);
  };

  const departamentos = ['People', 'TI', 'Comercial', 'Financeiro', 'CS', 'Marketing', 'Operacional'];
  const roles = ['Colaborador', 'Lider', 'G1', 'Master', 'Admin'];
  const contratos = ['CLT', 'PJ', 'Estagiário'];
  const statuses = ['active', 'inactive'];
  
  const exitTypes = [
      { value: 'voluntary', label: 'Voluntário (Pedido de Demissão)' },
      { value: 'involuntary', label: 'Involuntário (Demissão pela Empresa)' },
      { value: 'contract_end', label: 'Término de Contrato' }
  ];

  const exitReasons = [
      { value: 'salary', label: 'Proposta Salarial Melhor' },
      { value: 'career', label: 'Falta de Oportunidade de Crescimento' },
      { value: 'leadership', label: 'Problemas com Liderança' },
      { value: 'culture', label: 'Adaptação Cultural' },
      { value: 'performance', label: 'Baixo Desempenho' },
      { value: 'restructuring', label: 'Reestruturação / Corte' },
      { value: 'personal', label: 'Motivos Pessoais' },
      { value: 'other', label: 'Outros' }
  ];

  const modalTitle = isNewUser ? 'Pré-Cadastrar Novo Usuário' : 'Editar Usuário';
  const submitButtonText = isNewUser ? 'Salvar Pré-Cadastro' : 'Salvar Alterações';
  const inviteLabel = isNewUser ? "Enviar convite de acesso?" : `Enviar email de redefinição de senha para ${user?.email || ''}?`;
  const historicoDoUsuario = user?.historicoAtualizacoes || [];

  return (
    <div className="cadastro-modal-overlay">
      <div className="cadastro-modal">
        <div className="cadastro-modal-header">
          <h2>{modalTitle}</h2>
          <button onClick={onClose} className="cadastro-modal-close-button">&times;</button>
        </div>
        
        <div className="cadastro-modal-body">
          <form onSubmit={handleSubmit} className="cadastro-modal-form">
            
            {/* 1. Informações Pessoais & Endereço */}
            <div className="cadastro-form-section">
              <h4>Dados Pessoais & Endereço</h4>
              <div className="cadastro-form-group">
                <label htmlFor="name">Nome Completo:</label>
                <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required />
              </div>
              <div className="cadastro-form-group">
                <label htmlFor="email">Email:</label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required disabled={!isNewUser && user?.email} />
              </div>
              <div className="cadastro-form-group">
                <label htmlFor="dataNascimento">Data de Nascimento:</label>
                <input type="date" id="dataNascimento" name="dataNascimento" value={formData.dataNascimento} onChange={handleChange} />
              </div>
              
              {/* --- BLOCO DE ENDEREÇO NOVO --- */}
              <div className="cadastro-form-row" style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <div className="cadastro-form-group" style={{ flex: '1' }}>
                    <label htmlFor="cep">CEP:</label>
                    <input 
                        type="text" 
                        id="cep" 
                        name="cep" 
                        value={formData.cep} 
                        onChange={handleChange} 
                        onBlur={handleBlurCep} 
                        placeholder="00000-000"
                        maxLength="9"
                    />
                    {loadingCep && <span style={{fontSize:'0.8em', color:'#666'}}>Buscando...</span>}
                </div>
                <div className="cadastro-form-group" style={{ flex: '2' }}>
                    <label htmlFor="endereco">Endereço (Rua):</label>
                    <input type="text" id="endereco" name="endereco" value={formData.endereco} onChange={handleChange} disabled />
                </div>
                <div className="cadastro-form-group" style={{ flex: '0.5' }}>
                    <label htmlFor="numero">Número:</label>
                    <input type="text" id="numero" name="numero" value={formData.numero} onChange={handleChange} />
                </div>
              </div>
              
              <div className="cadastro-form-row" style={{ display: 'flex', gap: '10px' }}>
                <div className="cadastro-form-group" style={{ flex: '1' }}>
                    <label htmlFor="bairro">Bairro:</label>
                    <input type="text" id="bairro" name="bairro" value={formData.bairro} onChange={handleChange} disabled />
                </div>
                <div className="cadastro-form-group" style={{ flex: '1' }}>
                    <label htmlFor="cidade">Cidade:</label>
                    <input type="text" id="cidade" name="cidade" value={formData.cidade} onChange={handleChange} disabled />
                </div>
                <div className="cadastro-form-group" style={{ flex: '0.3' }}>
                    <label htmlFor="estado">UF:</label>
                    <input type="text" id="estado" name="estado" value={formData.estado} onChange={handleChange} disabled />
                </div>
              </div>
               {/* ------------------------------- */}

            </div>

            {/* 2. Dados Corporativos */}
            <div className="cadastro-form-section">
              <h4>Corporativo & Hierarquia</h4>
              <div className="cadastro-form-group">
                <label htmlFor="departamento">Departamento:</label>
                <select id="departamento" name="departamento" value={formData.departamento} onChange={handleChange} required>
                  <option value="" disabled>Selecione...</option>
                  {departamentos.map(dep => <option key={dep} value={dep}>{dep}</option>)}
                </select>
              </div>
              <div className="cadastro-form-group">
                <label htmlFor="role">Nível de Acesso (Role):</label>
                <select id="role" name="role" value={formData.role} onChange={handleChange} required>
                  <option value="" disabled>Selecione...</option>
                  {roles.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="cadastro-form-group">
                <label htmlFor="funcao">Função / Cargo:</label>
                <input type="text" id="funcao" name="funcao" value={formData.funcao} onChange={handleChange} />
              </div>
              <div className="cadastro-form-group">
                <label htmlFor="loja">Loja / Unidade:</label>
                <input type="text" id="loja" name="loja" value={formData.loja} onChange={handleChange} placeholder="Ex: Matriz, Loja 01..." />
              </div>
              
              {/* Seleção de Gestor */}
              <div className="cadastro-form-group">
                <label htmlFor="managerEmail">Gestor (Reporta a):</label>
                <select id="managerEmail" name="managerEmail" value={formData.managerEmail} onChange={handleChange}>
                  <option value="">-- Sem Gestor Direto --</option>
                  {allUsers && allUsers
                    .filter(u => u.email !== formData.email) 
                    .map(u => (
                      <option key={u.id} value={u.email}>
                        {u.name} ({u.email})
                      </option>
                  ))}
                </select>
              </div>

              <div className="cadastro-form-group checkbox" style={{marginTop: '10px'}}>
                  <input type="checkbox" id="canViewSubordinates" name="canViewSubordinates" checked={formData.canViewSubordinates} onChange={handleChange} />
                  <label htmlFor="canViewSubordinates">Gestor: Pode visualizar dados sensíveis de subordinados?</label>
              </div>
            </div>

            {/* 3. Contrato e Financeiro */}
            <div className="cadastro-form-section">
              <h4>Contrato e Remuneração</h4>
              <div className="cadastro-form-group">
                <label htmlFor="modeloContrato">Modelo de Contrato:</label>
                <select id="modeloContrato" name="modeloContrato" value={formData.modeloContrato} onChange={handleChange}>
                  {contratos.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="cadastro-form-group">
                <label htmlFor="dataEntrada">Data de Admissão:</label>
                <input type="date" id="dataEntrada" name="dataEntrada" value={formData.dataEntrada} onChange={handleChange} />
              </div>
              <div className="cadastro-form-group">
                <label htmlFor="salario">Salário Bruto (R$):</label>
                <input type="number" id="salario" name="salario" value={formData.salario} onChange={handleChange} step="0.01" />
              </div>

              <div className="cadastro-dynamic-list">
                <label>Benefícios:</label>
                {formData.beneficios.map((item, index) => (
                  <div key={`beneficio-${index}`} className="cadastro-dynamic-list-item">
                    <input type="text" name="nome" placeholder="Nome" value={item.nome} onChange={(e) => handleItemChange(index, e, 'beneficios')} className="dynamic-item-nome" />
                    <input type="number" name="valor" placeholder="Valor" value={item.valor} onChange={(e) => handleItemChange(index, e, 'beneficios')} className="dynamic-item-valor" step="0.01" />
                    {formData.beneficios.length > 1 && <button type="button" onClick={() => handleRemoveItem(index, 'beneficios')} className="cadastro-remove-item-button">&ndash;</button>}
                  </div>
                ))}
                <button type="button" onClick={() => handleAddItem('beneficios')} className="cadastro-add-item-button">&#43; Adicionar</button>
              </div>

              <div className="cadastro-dynamic-list">
                <label>Descontos:</label>
                {formData.descontos.map((item, index) => (
                  <div key={`desconto-${index}`} className="cadastro-dynamic-list-item">
                    <input type="text" name="nome" placeholder="Nome" value={item.nome} onChange={(e) => handleItemChange(index, e, 'descontos')} className="dynamic-item-nome" />
                    <input type="number" name="valor" placeholder="Valor" value={item.valor} onChange={(e) => handleItemChange(index, e, 'descontos')} className="dynamic-item-valor" step="0.01" />
                    {formData.descontos.length > 1 && <button type="button" onClick={() => handleRemoveItem(index, 'descontos')} className="cadastro-remove-item-button">&ndash;</button>}
                  </div>
                ))}
                <button type="button" onClick={() => handleAddItem('descontos')} className="cadastro-add-item-button">&#43; Adicionar</button>
              </div>
            </div>
            
            {/* 4. Status e Desligamento */}
            <div className="cadastro-form-section">
              <h4>Status e Acesso</h4>
              {!isNewUser && user && (
                <div className="cadastro-form-group">
                  <label htmlFor="status">Status do Usuário:</label>
                  <select id="status" name="status" value={formData.status} onChange={handleChange}>
                    {statuses.map(s => <option key={s} value={s}>{s === 'active' ? 'Ativo' : 'Inativo (Bloqueado)'}</option>)}
                  </select>
                </div>
              )}

              {formData.status === 'inactive' && (
                  <div className="exit-interview-container" style={{backgroundColor: '#fff3f3', padding: '15px', borderRadius: '8px', border: '1px solid #ffcdd2', marginTop: '15px'}}>
                      <h5 style={{color: '#c62828', marginTop: 0, marginBottom: '15px'}}>Dados de Desligamento (People Analytics)</h5>
                      
                      <div className="cadastro-form-group">
                          <label htmlFor="exitDate">Data de Saída:</label>
                          <input type="date" id="exitDate" name="exitDate" value={exitData.exitDate} onChange={handleExitDataChange} required />
                      </div>

                      <div className="cadastro-form-group">
                          <label htmlFor="type">Tipo de Desligamento:</label>
                          <select id="type" name="type" value={exitData.type} onChange={handleExitDataChange} required>
                              <option value="" disabled>Selecione...</option>
                              {exitTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                      </div>

                      <div className="cadastro-form-group">
                          <label htmlFor="reason">Motivo Principal:</label>
                          <select id="reason" name="reason" value={exitData.reason} onChange={handleExitDataChange} required>
                              <option value="" disabled>Selecione...</option>
                              {exitReasons.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                          </select>
                      </div>

                      <div className="cadastro-form-group">
                          <label htmlFor="wouldRecommend">Recomendaria a empresa?</label>
                          <select id="wouldRecommend" name="wouldRecommend" value={exitData.wouldRecommend} onChange={handleExitDataChange}>
                              <option value="yes">Sim</option>
                              <option value="no">Não</option>
                              <option value="maybe">Talvez</option>
                          </select>
                      </div>

                      <div className="cadastro-form-group">
                          <label htmlFor="feedback">Observações / Feedback:</label>
                          <textarea 
                            id="feedback" 
                            name="feedback" 
                            value={exitData.feedback} 
                            onChange={handleExitDataChange} 
                            rows="3" 
                            style={{width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ccc'}}
                            placeholder="Resumo da entrevista de desligamento..."
                          />
                      </div>
                  </div>
              )}

              {(isNewUser || (user && user.email)) && formData.status === 'active' &&
                <div className="cadastro-form-group checkbox">
                    <input type="checkbox" id="sendInvite" name="sendInvite" checked={formData.sendInvite} onChange={handleChange} />
                    <label htmlFor="sendInvite">{inviteLabel}</label>
                </div>
              }
            </div>

            {/* Histórico */}
            {!isNewUser && historicoDoUsuario.length > 0 && (
              <div className="cadastro-form-section">
                <h4>Histórico de Alterações</h4>
                <ul className="cadastro-historico-lista">
                  {historicoDoUsuario.slice().reverse().map((log, index) => (
                    <li key={`hist-${index}`} className="cadastro-historico-item">
                      <span className="historico-data">
                        {log.data && typeof log.data.toDate === 'function' 
                          ? log.data.toDate().toLocaleString('pt-BR')
                          : (log.data ? new Date(log.data).toLocaleString('pt-BR') : '-')}
                      </span>
                      <p className="historico-descricao">{log.alteracao}</p>
                      {log.responsavelNome && <span className="historico-responsavel">Por: {log.responsavelNome}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className="cadastro-modal-actions">
              <button type="button" onClick={onClose} className="cadastro-button secondary">Cancelar</button>
              <button type="submit" className="cadastro-button primary">{submitButtonText}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserFormModal;